// PATH: src/components/documents/useChapterReview.ts
/**
 * Données de relecture d'un chapitre, partagées par les onglets du panneau :
 * ses objectifs (onglet « Objectifs et faits ») et ses leçons (onglet
 * « Leçons »). Une extraction met à jour les deux.
 */
import * as React from "react";
import { useObjectivesApi } from "../../api/objectives/objectives";
import type { DocumentObjective, LessonsReport, ObjectivesResponse } from "../../api/objectives/objectives";
import { formatLessonsRun, type LessonsRunView } from "./lessonCheck";
import { formatReplacement } from "./objectivesView";

export type LoadState = "loading" | "ok" | "absent" | "error";

export interface ChapterReview {
  objectives: DocumentObjective[];
  objectivesState: LoadState;
  fromCache: boolean;
  lessons: LessonsReport | null;
  /** `absent` : pas de manifeste (404), donc pas de leçons. */
  lessonsState: LoadState;
  extracting: boolean;
  /** Bilan de la dernière extraction d'un document qui n'est pas un chapitre (upsert par provenance). */
  replacement: { text: string; severity: "success" | "warning" } | null;
  /** Bilan par leçon de la dernière extraction d'un chapitre importé. */
  lessonsRun: LessonsRunView | null;
  extractError: string | null;
  reloadObjectives: (quiet?: boolean) => void;
  reloadLessons: (quiet?: boolean) => void;
  extract: (force: boolean) => Promise<ObjectivesResponse | null>;
}

export const useChapterReview = (documentId: string | null): ChapterReview => {
  const { getObjectives, extractObjectives, getLessons } = useObjectivesApi();

  const [objectives, setObjectives] = React.useState<DocumentObjective[]>([]);
  const [objectivesState, setObjectivesState] = React.useState<LoadState>("loading");
  const [fromCache, setFromCache] = React.useState(false);
  const [lessons, setLessons] = React.useState<LessonsReport | null>(null);
  const [lessonsState, setLessonsState] = React.useState<LoadState>("loading");
  const [extracting, setExtracting] = React.useState(false);
  const [replacement, setReplacement] = React.useState<ChapterReview["replacement"]>(null);
  const [lessonsRun, setLessonsRun] = React.useState<LessonsRunView | null>(null);
  const [extractError, setExtractError] = React.useState<string | null>(null);

  // Réponse arrivée pour un autre chapitre (panneau changé entre-temps) : ignorée.
  const current = React.useRef(documentId);
  current.current = documentId;

  const reloadLessons = React.useCallback(
    (quiet = false) => {
      const id = documentId;
      if (!id) return;
      if (!quiet) setLessonsState("loading");
      getLessons(id)
        .then((res) => {
          if (current.current !== id) return;
          setLessons(res.data);
          setLessonsState("ok");
        })
        .catch((err) => {
          if (current.current !== id) return;
          setLessons(null);
          setLessonsState(err?.response?.status === 404 ? "absent" : "error");
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId],
  );

  const reloadObjectives = React.useCallback(
    (quiet = false) => {
      const id = documentId;
      if (!id) return;
      if (!quiet) setObjectivesState("loading");
      getObjectives(id)
        .then((res) => {
          if (current.current !== id) return;
          setObjectives(res.data.objectives ?? []);
          setFromCache(res.data.from_cache);
          setObjectivesState("ok");
        })
        .catch((err) => {
          if (current.current !== id) return;
          if (err?.response?.status === 404) {
            // Pas encore extraits : état vide attendu.
            setObjectives([]);
            setObjectivesState("ok");
          } else {
            setObjectivesState("error");
          }
        });
      // Les faits ont pu changer : le statut des leçons aussi.
      if (quiet) reloadLessons(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId, reloadLessons],
  );

  React.useEffect(() => {
    setObjectives([]);
    setLessons(null);
    setReplacement(null);
    setLessonsRun(null);
    setExtractError(null);
    setFromCache(false);
    if (!documentId) return;
    reloadObjectives();
    reloadLessons();
  }, [documentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const extract = async (force: boolean) => {
    const id = documentId;
    if (!id) return null;
    setExtracting(true);
    setExtractError(null);
    try {
      const res = await extractObjectives(id, force);
      if (current.current !== id) return null;
      setObjectives(res.data.objectives ?? []);
      setFromCache(res.data.from_cache);
      setObjectivesState("ok");
      // Chapitre importé : le bilan par leçon dit déjà repris / orphelins.
      const run = res.data.lessons;
      setLessonsRun(run ? formatLessonsRun(run, res.data) : null);
      setReplacement(run ? null : formatReplacement(res.data));
      reloadLessons(true);
      return res.data;
    } catch {
      setExtractError("L'extraction des objectifs a échoué.");
      return null;
    } finally {
      setExtracting(false);
    }
  };

  return {
    objectives,
    objectivesState,
    fromCache,
    lessons,
    lessonsState,
    extracting,
    replacement,
    lessonsRun,
    extractError,
    reloadObjectives,
    reloadLessons,
    extract,
  };
};
